import { Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useUnleashContext } from '@unleash/proxy-client-react';
import { default as React, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../app/hooks';
import useTaskRouterMenuHandlers from '../../hooks/taskRouterHandlers/useTaskRouterMenuHandlers.hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import {
  resetSelectedRestaurantDetails,
  selectRestaurant,
} from '../../reducers/restaurantSlice';
import { startLoading } from '../../redux/features/loading/loading.slice';
import {
  selectCurrentTask,
  selectCurrentTaskRestaurantCode,
  selectCurrentTaskRestaurantName,
} from '../../redux/features/taskRouter/taskRouter.selector';
import { selectRestaurantsById } from '../../selectors/restaurant';
import {
  selectConsolidatedMenuBrands,
  selectConsolidatedMenuExcludedRestaurants,
} from '../../redux/features/config/config.selector';
import RestaurantPicker from '../RestaurantPicker';
import { LANGUAGES } from '../../utils/constants';
import useLanguages from '../../hooks/useLanguages.hooks';
import { readEnvVariable } from '../../utils';
import { getFeatureFlagValues } from '../../utils/network';
import { setMenuUnificationFeatureFlag } from '../../utils/session-storage';
import { isConsolidatedMenuRestaurant } from '../../utils/restaurants';
import { updateMenuLoadingTimings } from '../../reducers/menuSlice';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  name: {
    fontWeight: '500 !important',
    fontSize: '0.75rem !important',
    lineHeight: '1.75 !important',
    textTransform: 'uppercase',
    minWidth: spacing(8),
    // Show the full name — never ellipsize (stays on the single header line).
    whiteSpace: 'nowrap',
    padding: `${spacing(0.75)} ${spacing(2)}`,
  },
}));

const RestaurantsContainer = ({
  logout,
  sendHealthScore,
}: {
  logout: Function;
  sendHealthScore?: Function;
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const updateContext = useUnleashContext();
  const isPlaying = useAppSelector((state) => state.messages.isPlaying);
  const currentTaskId = useAppSelector(selectCurrentTask);
  const {
    taskRouterMenuLoadFailureCallback,
    taskRouterMenuLoadSuccessCallback,
  } = useTaskRouterMenuHandlers();
  const currentTaskRestaurantCode = useAppSelector(
    selectCurrentTaskRestaurantCode
  );
  const currentTaskRestaurantName = useAppSelector(
    selectCurrentTaskRestaurantName
  );
  const restaurantsById = useAppSelector(selectRestaurantsById);
  const consolidatedMenuBrands = useAppSelector(selectConsolidatedMenuBrands);
  const consolidatedMenuExcludedRestaurants = useAppSelector(
    selectConsolidatedMenuExcludedRestaurants
  );
  const currentLanguage = useAppSelector(
    (state) => state.language.currentLanguage
  );
  const { sendLanguageSwitch } = useLanguages();

  const targetRestaurant = useMemo(() => {
    if (currentTaskRestaurantCode) {
      if (Object.keys(restaurantsById).length) {
        return Object.values(restaurantsById)?.filter(
          (restaurant) =>
            restaurant.restaurantCode === currentTaskRestaurantCode
        )?.[0];
      }
      return {
        restaurantCode: currentTaskRestaurantCode,
        restaurantName: currentTaskRestaurantName,
      };
    }
  }, [restaurantsById, currentTaskRestaurantCode, currentTaskRestaurantName]);

  const selectUpdatedRestaurant = async ({
    restaurantCode,
    startLoadingIndicator,
  }: {
    restaurantCode: string;
    startLoadingIndicator: boolean;
  }) => {
    // Show the loading indicator immediately — BEFORE the awaited Unleash
    // context refresh below — so the previous restaurant's UI (including the
    // feature-flag-driven layout) is never briefly visible during the switch.
    if (startLoadingIndicator) {
      dispatch(startLoading({}));
    }

    let unleashFlagDurationMs: number;

    if (
      isConsolidatedMenuRestaurant(
        restaurantCode,
        consolidatedMenuBrands,
        consolidatedMenuExcludedRestaurants
      )
    ) {
      // Consolidated-menu brands (config-listed) are always on the unified menu
      // — skip the blocking Unleash fetch, but still refresh the Unleash context
      // per-restaurant (fire and forget) so other `useFlag` consumers resolve
      // against the right userId.
      updateContext({ userId: restaurantCode });
      setMenuUnificationFeatureFlag('true');
      unleashFlagDurationMs = 0;
    } else {
      const unleashStartTime = performance.now();
      await updateContext({ userId: restaurantCode });
      const { unified_menu } = await getFeatureFlagValues({
        url: `${readEnvVariable('UNLEASH_API_URL')}?userId=${restaurantCode}`,
        names: ['unified_menu'],
      });
      setMenuUnificationFeatureFlag(unified_menu.toString());
      unleashFlagDurationMs = performance.now() - unleashStartTime;
    }

    // Dispatch selectRestaurant first — its fetchMenu thunk synchronously
    // resets menuLoadingTimings, so we set unleashFlagDurationMs after.
    dispatch(
      selectRestaurant({
        restaurantCode: restaurantCode,
        logout,
        taskRouterMenuLoadFailureCallback,
        taskRouterMenuLoadSuccessCallback,
      })
    );
    dispatch(updateMenuLoadingTimings({ unleashFlagDurationMs }));

    if (!isPlaying) {
      dispatch(messagingActions.setIsPlaying(true));
    }
  };

  const handleRestaurantSelect = (restaurantCode: string) => {
    sendHealthScore?.();
    if (currentTaskId) {
      selectUpdatedRestaurant({
        restaurantCode,
        startLoadingIndicator: false,
      });
      return;
    }
    dispatch(resetSelectedRestaurantDetails());
    selectUpdatedRestaurant({
      restaurantCode,
      startLoadingIndicator: true,
    });
    if (currentLanguage !== LANGUAGES.ENGLISH) {
      sendLanguageSwitch(LANGUAGES.ENGLISH);
    }
  };

  useEffect(() => {
    if (targetRestaurant) {
      handleRestaurantSelect(targetRestaurant.restaurantCode);
    }
  }, [targetRestaurant]);
  return (
    <div id="restaurants-container">
      {targetRestaurant ? (
        <Typography className={classes.name}>
          {targetRestaurant?.restaurantName}
        </Typography>
      ) : (
        <RestaurantPicker handleRestaurantSelect={handleRestaurantSelect} />
      )}
    </div>
  );
};

export default RestaurantsContainer;
